import { Button, Form, FormInstance, Select, Spin } from "antd";
import { useState, useCallback } from "react";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/ace";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/mode-xml";
import "ace-builds/src-noconflict/theme-tomorrow";
import { useTranslation } from "react-i18next";
import { ContentType, IntegrationFormData } from "../../../api";
import { validateJson, validateXml } from "../../../util/validation";
import { formatTemplate } from "../../../util/formatting";

interface TemplateFormItemProps {
  form: FormInstance<IntegrationFormData>;
  formDisabled: boolean;
  loading: boolean;
}

export const TemplateFormItem = ({
  form,
  formDisabled,
  loading,
}: TemplateFormItemProps) => {
  const { t } = useTranslation();
  const [templateValidationError, setTemplateValidationError] = useState<
    string | null
  >(null);

  // Watch for content type changes to set the proper mode for the ace editor
  const templateContentType = Form.useWatch("templateContentType", form);

  const validateTemplate = async (_: unknown, value: string): Promise<void> => {
    if (!value) {
      setTemplateValidationError(t("translations:required"));
      return Promise.reject(new Error(undefined));
    }

    const currentContentType = form.getFieldValue("templateContentType");

    if (currentContentType === ContentType.JSON) {
      if (validateJson(value)) {
        setTemplateValidationError(null);
        return Promise.resolve();
      } else {
        const errorMessage = t("translations:validation-error-json");
        setTemplateValidationError(errorMessage);
        return Promise.reject(new Error(undefined));
      }
    } else if (
      currentContentType === ContentType.APPXML ||
      currentContentType === ContentType.TEXTXML
    ) {
      if (validateXml(value)) {
        setTemplateValidationError(null);
        return Promise.resolve();
      } else {
        const errorMessage = t("translations:validation-error-xml");
        setTemplateValidationError(errorMessage);
        return Promise.reject(new Error(undefined));
      }
    }

    return Promise.resolve();
  };

  const onFormatTemplate = useCallback(
    (contentType: string | undefined) => {
      const content = form.getFieldValue("templateContent");

      const formattedContent = formatTemplate(contentType, content);

      form.setFieldsValue({ templateContent: formattedContent });
    },
    [form]
  );

  const getAceEditorMode = () => {
    if (templateContentType === ContentType.JSON) {
      return "json";
    } else if (
      templateContentType === ContentType.APPXML ||
      templateContentType === ContentType.TEXTXML
    ) {
      return "xml";
    }
    return "xml"; // Default to XML if content type is not set or recognized
  };

  return (
    <div data-testid="template-form-item">
      <span>
        <b>{t("integrations:temp-label")}</b>
        <span style={{ color: "red" }}> {templateValidationError} </span>
      </span>
      <div style={{ border: "1px solid #d9d9d9" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Form.Item
            name="templateContentType"
            rules={[{ required: true, message: t("translations:required") }]}
            style={{ marginBottom: 0 }}
            initialValue={Object.values(ContentType)[0]}
          >
            <Select
              style={{ width: 150 }}
              disabled={formDisabled}
              data-testid="content-type-select"
            >
              {Object.values(ContentType).map((type) => (
                <Select.Option key={type} value={type}>
                  {type}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Button
            type="primary"
            style={{ borderRadius: "0 0 0 2px" }}
            onClick={() => onFormatTemplate(templateContentType)}
            disabled={formDisabled || templateValidationError !== null}
          >
            {t("format")}
          </Button>
        </div>
        <Spin spinning={loading}>
          <Form.Item
            name="templateContent"
            style={{ marginBottom: 0 }}
            rules={[{ validator: validateTemplate }]}
          >
            <AceEditor
              mode={getAceEditorMode()}
              theme="tomorrow"
              value={form.getFieldValue("templateContent") || ""}
              onChange={(text) =>
                form.setFieldsValue({ templateContent: text })
              }
              width="100%"
              height="300px"
              fontSize={14}
              setOptions={{
                tabSize: 2,
                useWorker: false,
              }}
            />
          </Form.Item>
        </Spin>
      </div>
    </div>
  );
};
